<script setup lang="ts">
/* UTILS  */
/* COMPONENTS */
import { AfAssessmentsStatus } from '@af-lib/ui-lib';
import { ALAvatar, ALButton } from '@af-lib/vue-ui';

type ALAvatarSize = InstanceType<typeof ALAvatar>['$props']['size'];
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import Text from '@/components/text/Text.vue';
import { t } from '@/config/i18n';
/* HELPERS */
import { getAvatarPath, getFullnameOrEmail, getJobName } from '@/helpers/users';
import { useGlobalStore } from '@/stores/global';
/* TYPES */
import type { UserProfileCard } from '@/types/api/user/user';
/* STORE */
import type { AssessmentWithVoice } from '@/types/assessments/assessments.types';
import type { VuetifyColorName } from '@/types/colors/colors.types';

type Props = {
  // MANDATORY : user data
  user: UserProfileCard;
  // OPTIONAL: size of the avatar
  avatarSize?: ALAvatarSize;
  // OPTIONAL: true to hide job position
  noJob?: boolean;
  // OPTIONAL: true to hide email
  noEmail?: boolean;
  // Optional - true to hide location
  noLocation?: boolean;
  // Optional - aplayer score if it should be displayed
  aPlayerScore?: number | null;
  // Optional - display as column instead of row
  flexColumn?: boolean;
  datacyPrefix?: string;
  showAssessments?: boolean;
  showEmployeeFlag?: boolean;
  assessmentsList?: AssessmentWithVoice[];
  isButton?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  avatarSize: 'm',
  noJob: false,
  noEmail: false,
  noLocation: true,
  showAssessments: false,
  showEmployeeFlag: true,
  assessmentsList: () => ['shape', 'drive', 'brain'],
  isButton: false
});

const router = useRouter();

const emits = defineEmits(['onButtonClick']);

const userUUID = props.user.user_uuid || props.user.uuid;

const userFullName = computed(() => {
  if (props.user.deleted_at) {
    return t('general.information.account_deleted');
  }
  return getFullnameOrEmail(props.user) || t('general.information.redact');
});

const profileRoute = computed(() => {
  return { name: 'profile', params: { uuid: userUUID } };
});

function redirectToProfile() {
  router.push(profileRoute.value);
}

defineExpose({ redirectToProfile });
</script>

<template>
  <v-card
    flat
    :class="['profile-card d-flex', { 'flex-column align-center': props.flexColumn }]"
    variant="text"
    :border="0"
    color="transparent"
    :ripple="false"
  >
    <ALAvatar
      :firstName="props.user.first_name ?? ''"
      :lastName="props.user.last_name ?? ''"
      :gradients="useGlobalStore().getPersonalityGradients(props.user.disc_id)"
      :avatarUrl="getAvatarPath(props.user.branding) || ''"
      :size="props.avatarSize"
      clickable
      :aPlayerScore="props.aPlayerScore ? props.aPlayerScore : undefined"
    />

    <div
      :class="[
        'd-flex flex-column dataContainer',
        props.flexColumn ? 'align-center mt-3 w-100' : 'justify-center ml-3'
      ]"
      style="gap: 4px"
    >
      <div :class="['d-flex w-100', { 'justify-center': props.flexColumn }]">
        <ALButton
          v-if="isButton"
          type="quaternary"
          class="min-w-0 max-w-full [&>button>div]:min-w-0"
          @click="emits('onButtonClick')"
        >
          <span class="block w-full truncate">{{ userFullName }}</span>
        </ALButton>

        <Text
          v-else
          type="link2"
          emphasis="medium"
          class="profile-details text-truncate"
          :data-cy="props.datacyPrefix ? `${props.datacyPrefix}-name` : undefined"
        >
          {{ userFullName }}
        </Text>
      </div>

      <Text
        v-if="!props.noJob"
        type="body4"
        :class="['profile-details text-fontSecondary text-truncate', { 'mt-1': props.flexColumn }]"
        :data-cy="props.datacyPrefix ? `${props.datacyPrefix}-job` : undefined"
      >
        {{ getJobName(props.user) }}
      </Text>
      <Text
        v-if="!props.noEmail"
        type="caption1"
        :class="['text-fontSecondary profile-details w-100 d-flex', { 'justify-center mt-1': props.flexColumn }]"
      >
        <div class="text-truncate">{{ props.user.email }}</div>
      </Text>
      <!-- LOCATION -->
      <div v-if="!props.noLocation && props.user.locationName" class="d-flex align-center">
        <v-icon icon="fat fa-location-dot" color="fontSecondary" class="me-2 text-icon-4" />
        <Text type="body4"> {{ props.user.locationName }} </Text>
      </div>

      <AfAssessmentsStatus
        v-if="props.showAssessments"
        :requiredAssessments="props.assessmentsList"
        :backgroundColor="'transparent' as VuetifyColorName"
        :statuses="props.user"
        size="s"
      ></AfAssessmentsStatus>
    </div>
  </v-card>
</template>

<style scoped lang="scss">
.profile-card {
  width: fit-content;
  min-width: 0;
  max-width: 100%;
  text-decoration: none;
  border-radius: 0px;
}
.dataContainer {
  margin-inline-start: 12px;
  white-space: nowrap;
  width: 100%;
  overflow: hidden;
  text-overflow: clip;
}

.profile-card .profile-details {
  max-width: 100%;
}

.link:deep(span) {
  display: block;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
